import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GitMerge,
  EyeOff,
  RefreshCw,
  ShieldCheck,
  CheckCircle2,
  Layers,
  Users,
  Search,
  Phone,
  Mail,
  Sparkles,
  ArrowRight,
  Clock,
  Building2,
  AlertTriangle,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { crmApi } from "./api";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { Modal } from "../../components/ui/modal";
import { useToast } from "../../components/ui/toast";
import { CopyButton } from "../../components/ui/copy-button";
import { initialsOf, type DuplicateGroup, type DuplicateMember } from "./types";

function getRecommendedKeeperId(leads: DuplicateMember[]): string {
  if (!leads.length) return "";
  const sorted = [...leads].sort((a, b) => {
    // 1. More call interactions
    if ((b.interactions || 0) !== (a.interactions || 0)) {
      return (b.interactions || 0) - (a.interactions || 0);
    }
    // 2. Has notes
    if (!!b.notes !== !!a.notes) {
      return b.notes ? 1 : -1;
    }
    // 3. Oldest record (earliest created date)
    if (a.createdAt && b.createdAt) {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }
    return 0;
  });
  return sorted[0]?.id || leads[0].id;
}

function formatDate(iso?: string) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
}

export function DuplicatesPage() {
  const navigate = useNavigate();
  const cache = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<"all" | "phone" | "email">("all");
  const [merging, setMerging] = useState<DuplicateGroup | null>(null);
  const [keepId, setKeepId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [skipping, setSkipping] = useState<DuplicateGroup | null>(null);

  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });
  const canManage = team.data?.role === "admin" || !!team.data?.permissions?.assign_leads;

  const groups = useQuery({
    queryKey: ["crm-duplicates"],
    queryFn: crmApi.duplicates,
    enabled: canManage,
  });

  const refresh = async () => {
    await cache.invalidateQueries({ queryKey: ["crm-duplicates"] });
    toast.success("Duplicate check complete", "Latest records scanned.");
  };

  const openMerge = (group: DuplicateGroup) => {
    setMerging(group);
    setKeepId(getRecommendedKeeperId(group.leads));
    setError("");
  };

  const merge = async () => {
    if (!merging || busy) return;
    const otherIds = merging.leads.map((l) => l.id).filter((id) => id !== keepId);
    if (!keepId || otherIds.length === 0) {
      setError("Choose the primary record to keep.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await crmApi.mergeDuplicates(keepId, otherIds);
      await cache.invalidateQueries({ queryKey: ["crm-duplicates"] });
      await cache.invalidateQueries({ queryKey: ["crm-leads"] });
      toast.success("Duplicates merged successfully", "History and notes preserved into keeper.");
      setMerging(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to merge records.");
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    if (!skipping) return;
    try {
      await crmApi.skipDuplicates(skipping.key);
      await cache.invalidateQueries({ queryKey: ["crm-duplicates"] });
      toast.success("Group dismissed", `"${skipping.value}" marked as not duplicate.`);
      setSkipping(null);
    } catch (e) {
      toast.danger("Couldn't dismiss group", e instanceof Error ? e.message : "Unable to skip.");
      setSkipping(null);
    }
  };

  const allGroups = useMemo(() => groups.data ?? [], [groups.data]);
  const totalDupes = allGroups.reduce((n, g) => n + g.leads.length, 0);
  const phoneGroups = allGroups.filter((g) => g.field === "phone").length;
  const emailGroups = allGroups.filter((g) => g.field === "email").length;

  // Filtered groups
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allGroups.filter((g) => {
      if (filterType !== "all" && g.field !== filterType) return false;
      if (!q) return true;
      if (g.value.toLowerCase().includes(q)) return true;
      return g.leads.some(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          (l.phone && l.phone.toLowerCase().includes(q)) ||
          (l.email && l.email.toLowerCase().includes(q)) ||
          (l.company && l.company.toLowerCase().includes(q)),
      );
    });
  }, [allGroups, filterType, search]);

  const recommendedKeeper = useMemo(() => {
    if (!merging) return null;
    return merging.leads.find((l) => l.id === keepId) || null;
  }, [merging, keepId]);

  return (
    <div className="flex h-full flex-col p-4 sm:p-6 space-y-4">
      {/* 1. Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <h1 className="text-base font-semibold text-ink dark:text-white">Duplicates</h1>
          {allGroups.length > 0 && (
            <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400 border border-amber-500/20">
              {allGroups.length} {allGroups.length === 1 ? "conflict" : "conflicts"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void refresh()}
            disabled={groups.isFetching}
            className="flex items-center gap-1.5 rounded-lg border border-line dark:border-white/10 bg-elevated dark:bg-[#262320] px-3.5 py-1.5 text-xs font-semibold text-ink dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-white/5 active:scale-[0.98] transition-all disabled:opacity-60"
          >
            <RefreshCw className={cn("h-3.5 w-3.5 text-brand", groups.isFetching && "animate-spin")} />
            <span>{groups.isFetching ? "Scanning…" : "Rescan"}</span>
          </button>
        </div>
      </div>

      {/* 2. Top Summary KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Total Groups */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Duplicate Groups</span>
            <Layers className="h-3.5 w-3.5 text-brand" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">{allGroups.length}</span>
            <span className="text-[11px] text-ink-3 dark:text-stone-400">conflicts</span>
          </div>
          <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">
            {allGroups.length === 0 ? "No active matches" : "Awaiting merge review"}
          </div>
        </div>

        {/* Conflicting Leads */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Affected Leads</span>
            <Users className="h-3.5 w-3.5 text-brand" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">{totalDupes}</span>
            <span className="text-[11px] text-ink-3 dark:text-stone-400">contacts</span>
          </div>
          <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">
            {totalDupes === 0 ? "Zero cross-references" : "Sharing contact points"}
          </div>
        </div>

        {/* Database Hygiene */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Database Hygiene</span>
            {allGroups.length === 0 ? (
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            )}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">
              {allGroups.length === 0 ? "100%" : "Action"}
            </span>
            <span
              className={cn(
                "text-[11px] font-semibold",
                allGroups.length === 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-amber-600 dark:text-amber-400",
              )}
            >
              {allGroups.length === 0 ? "Unique" : "Needed"}
            </span>
          </div>
          <div className="mt-1 text-[11px] text-ink-3 dark:text-stone-400">
            {allGroups.length === 0 ? "No overlapping records" : `${allGroups.length} groups to resolve`}
          </div>
        </div>

        {/* Conflict Type Breakdown */}
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 shadow-card dark:shadow-card-dark">
          <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-400 mb-1">
            <span className="font-medium">Conflict Types</span>
            <GitMerge className="h-3.5 w-3.5 text-brand" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-ink dark:text-white">
              {phoneGroups + emailGroups}
            </span>
            <span className="text-[11px] text-ink-3 dark:text-stone-400">total</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-ink-3 dark:text-stone-400">
            <span>{phoneGroups} phone</span>
            <span>·</span>
            <span>{emailGroups} email</span>
          </div>
        </div>
      </div>

      {!canManage && team.data ? (
        <div className="rounded-xl border border-line dark:border-white/10 bg-elevated dark:bg-[#201e1b] p-6 text-center text-sm text-ink-3 dark:text-stone-400">
          Duplicate review is restricted to sales managers and administrators with lead assignment permissions.
        </div>
      ) : (
        <>
          {groups.error && (
            <div
              role="alert"
              className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-4 text-xs font-semibold text-red-600 dark:text-red-400"
            >
              {(groups.error as Error).message}
            </div>
          )}

          {/* 3. Empty State — Database Clean */}
          {!groups.isLoading && allGroups.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#201e1b] px-6 py-12 text-center shadow-card dark:shadow-card-dark">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 mb-4">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h2 className="text-base font-semibold text-ink dark:text-white">Database is 100% Unique</h2>
              <p className="mt-1.5 max-w-md text-xs text-ink-3 dark:text-stone-400 leading-relaxed">
                Every phone number and email address belongs to exactly one lead record. Automated scans run on all imports and manually registered contacts.
              </p>
              <div className="mt-5 flex items-center gap-2.5">
                <button
                  onClick={() => void refresh()}
                  className="flex items-center gap-1.5 rounded-lg border border-line dark:border-white/10 bg-white dark:bg-stone-800 px-3.5 py-1.5 text-xs font-semibold text-ink dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-white/5 active:scale-[0.98] transition-all"
                >
                  <RefreshCw className={cn("h-3.5 w-3.5 text-brand", groups.isFetching && "animate-spin")} />
                  Scan again
                </button>
                <button
                  onClick={() => navigate("/crm/leads")}
                  className="flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover active:scale-[0.98] transition-all"
                >
                  <span>View all leads</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* 4. Controls & Cards (when duplicates exist) */}
          {allGroups.length > 0 && (
            <>
              {/* Filter and Search Bar */}
              <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-2.5 shadow-card dark:shadow-card-dark">
                {/* Type Filter Buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setFilterType("all")}
                    className={cn(
                      "rounded-lg px-3 py-1.5 text-xs font-semibold transition-all",
                      filterType === "all"
                        ? "bg-brand text-white shadow-xs"
                        : "text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white",
                    )}
                  >
                    All ({allGroups.length})
                  </button>
                  <button
                    onClick={() => setFilterType("phone")}
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all",
                      filterType === "phone"
                        ? "bg-brand text-white shadow-xs"
                        : "text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white",
                    )}
                  >
                    <Phone className="h-3 w-3" />
                    Phone ({phoneGroups})
                  </button>
                  <button
                    onClick={() => setFilterType("email")}
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all",
                      filterType === "email"
                        ? "bg-brand text-white shadow-xs"
                        : "text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white",
                    )}
                  >
                    <Mail className="h-3 w-3" />
                    Email ({emailGroups})
                  </button>
                </div>

                {/* Search */}
                <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3 dark:text-stone-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, phone, email…"
                    className="w-full rounded-lg border border-line dark:border-white/10 bg-white dark:bg-[#1a1816] py-1.5 pl-8 pr-3 text-xs text-ink dark:text-white placeholder:text-ink-3 dark:placeholder:text-stone-500 focus:border-brand focus:outline-none"
                  />
                </div>
              </div>

              {/* Group Cards Grid */}
              <div className="grid gap-3.5 lg:grid-cols-2">
                {filteredGroups.map((group) => {
                  const recId = getRecommendedKeeperId(group.leads);
                  return (
                    <div
                      key={group.key}
                      className="flex flex-col justify-between rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#201e1b] p-4 sm:p-5 shadow-card dark:shadow-card-dark transition-all hover:border-brand/30"
                    >
                      <div>
                        {/* Top Group Meta Bar */}
                        <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-line dark:border-white/10">
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                "flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold border",
                                group.field === "phone"
                                  ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20"
                                  : "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
                              )}
                            >
                              {group.field === "phone" ? (
                                <>
                                  <Phone className="h-3 w-3" /> Same Phone
                                </>
                              ) : (
                                <>
                                  <Mail className="h-3 w-3" /> Same Email
                                </>
                              )}
                            </span>
                            <div className="flex items-center gap-1 font-mono text-xs font-semibold text-ink dark:text-white">
                              <span>{group.value}</span>
                              <CopyButton value={group.value} />
                            </div>
                          </div>
                          <span className="rounded-full bg-stone-100 dark:bg-stone-800 px-2 py-0.5 text-[11px] font-semibold text-ink-3 dark:text-stone-400">
                            {group.leads.length} records
                          </span>
                        </div>

                        {/* Leads Comparison List */}
                        <div className="divide-y divide-line dark:divide-white/10">
                          {group.leads.map((lead) => {
                            const isRecommended = lead.id === recId;
                            return (
                              <div
                                key={lead.id}
                                className={cn(
                                  "flex items-start gap-3 py-3 transition-colors",
                                  isRecommended && "bg-emerald-500/5 -mx-4 sm:-mx-5 px-4 sm:px-5",
                                )}
                              >
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-stone-200 dark:bg-stone-700 text-xs font-semibold text-ink dark:text-white">
                                  {initialsOf(lead.name)}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-xs font-semibold text-ink dark:text-white">
                                      {lead.name || "Unnamed lead"}
                                    </span>
                                    {isRecommended && (
                                      <span className="flex items-center gap-0.5 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                        <Sparkles className="h-2.5 w-2.5" />
                                        Recommended keeper
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-3 dark:text-stone-400">
                                    {lead.company && (
                                      <span className="flex items-center gap-1">
                                        <Building2 className="h-2.5 w-2.5" />
                                        {lead.company}
                                      </span>
                                    )}
                                    {lead.phone && <span>{lead.phone}</span>}
                                    {lead.email && <span>{lead.email}</span>}
                                    {lead.createdAt && (
                                      <span className="flex items-center gap-1">
                                        <Clock className="h-2.5 w-2.5" />
                                        {formatDate(lead.createdAt)}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="shrink-0 text-right">
                                  <div className="text-[11px] font-semibold text-ink dark:text-white">
                                    {lead.interactions} {lead.interactions === 1 ? "call" : "calls"}
                                  </div>
                                  <div className="text-[10px] text-ink-3 dark:text-stone-400">
                                    {lead.notes ? "Has notes" : "No notes"}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Card Footer Actions */}
                      <div className="mt-2 flex items-center justify-between border-t border-line dark:border-white/10 pt-3">
                        <button
                          onClick={() => openMerge(group)}
                          className="flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover active:scale-[0.98] transition-all"
                        >
                          <GitMerge className="h-3.5 w-3.5" />
                          <span>Review & Merge…</span>
                        </button>
                        <button
                          onClick={() => setSkipping(group)}
                          className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-ink-3 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-white/5 hover:text-ink dark:hover:text-white transition-all"
                        >
                          <EyeOff className="h-3.5 w-3.5" />
                          <span>Not duplicate</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* 5. Enhanced Merge Modal */}
      {merging && (
        <Modal
          title="Merge duplicate records"
          subtitle="Choose which record to keep as the primary. All interaction logs, notes, and activity history from the other records will be merged into it."
          onClose={() => !busy && setMerging(null)}
          width="max-w-xl"
          footer={
            <div className="flex w-full items-center justify-between">
              <div className="text-xs text-ink-3 dark:text-stone-400">
                {merging.leads.length - 1} secondary {merging.leads.length - 1 === 1 ? "record" : "records"} will be merged
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={busy}
                  onClick={() => setMerging(null)}
                  className="rounded-lg px-3.5 py-1.5 text-xs font-semibold text-ink-2 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-white/5 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  disabled={busy}
                  onClick={() => void merge()}
                  className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover disabled:opacity-50 transition-all active:scale-[0.98]"
                >
                  <GitMerge className="h-3.5 w-3.5" />
                  <span>{busy ? "Merging records…" : "Confirm Merge"}</span>
                </button>
              </div>
            </div>
          }
        >
          <div className="space-y-3">
            <div className="text-xs font-medium text-ink-2 dark:text-stone-300 mb-1">
              Select Primary Record (Keeper):
            </div>

            <div className="space-y-2">
              {merging.leads.map((lead) => {
                const isSelected = keepId === lead.id;
                const recId = getRecommendedKeeperId(merging.leads);
                const isRecommended = lead.id === recId;

                return (
                  <label
                    key={lead.id}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-all",
                      isSelected
                        ? "border-brand bg-brand/5 dark:bg-brand/10 ring-1 ring-brand"
                        : "border-line dark:border-white/10 hover:bg-stone-50 dark:hover:bg-white/5",
                    )}
                  >
                    <input
                      type="radio"
                      name="keeper"
                      checked={isSelected}
                      onChange={() => setKeepId(lead.id)}
                      className="mt-1 h-4 w-4 accent-brand cursor-pointer"
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-ink dark:text-white">
                          {lead.name || "Unnamed lead"}
                        </span>
                        {isRecommended && (
                          <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                            <Sparkles className="h-2.5 w-2.5" /> Best Match
                          </span>
                        )}
                        {isSelected && (
                          <span className="rounded-full bg-brand px-2 py-0.5 text-[10px] font-semibold text-white">
                            Primary Record
                          </span>
                        )}
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 font-mono text-[11px] text-ink-3 dark:text-stone-400">
                        {lead.phone && <span>{lead.phone}</span>}
                        {lead.email && <span>{lead.email}</span>}
                        {lead.company && <span>· {lead.company}</span>}
                        {lead.createdAt && <span>· Created {formatDate(lead.createdAt)}</span>}
                      </div>

                      <div className="mt-2 flex items-center gap-3 text-[11px] text-ink-3 dark:text-stone-400 border-t border-line dark:border-white/10 pt-1.5">
                        <span className="font-semibold text-ink dark:text-stone-200">
                          {lead.interactions} call interaction{lead.interactions === 1 ? "" : "s"}
                        </span>
                        <span>·</span>
                        <span>{lead.notes ? "Contains notes" : "No notes"}</span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {error && (
              <p role="alert" className="text-xs font-semibold text-red-600 dark:text-red-400 mt-2">
                {error}
              </p>
            )}

            <div className="rounded-lg bg-stone-50 dark:bg-white/5 border border-line dark:border-white/10 p-3 text-[11px] text-ink-3 dark:text-stone-400 leading-relaxed">
              <span className="font-semibold text-ink dark:text-stone-200">Notice:</span> When merging, all call recordings, notes, tasks, and history from secondary records will be transferred into the selected keeper. The secondary record entries are then permanently removed.
            </div>
          </div>
        </Modal>
      )}

      {/* 6. Skip Confirmation Modal */}
      {skipping && (
        <ConfirmModal
          title="Mark as not duplicate?"
          body={`"${skipping.value}" will be dismissed from the duplicates list. These records will continue to exist as distinct contacts in your CRM.`}
          confirmLabel="Dismiss group"
          onConfirm={() => void skip()}
          onClose={() => setSkipping(null)}
        />
      )}
    </div>
  );
}

